/**
 * **Das Lebenszeichen des Fokus-Fensters.**
 *
 * Gerätelokal (`localStorage`) und ausdrücklich **nicht** synchronisiert: Es
 * ist keine Aussage über den Fokus, sondern über diesen Rechner — „bis hierher
 * lief das Fokus-Fenster noch". Ein abgestürzter Rechner hinterlässt einen laufenden
 * Abschnitt ohne Ende; beim nächsten Start wird bis zum letzten Lebenszeichen
 * gebucht statt bis jetzt, sonst zählte die Nacht als Fokuszeit.
 *
 * Dieselbe Zwei-Minuten-Regel gilt im Web für den geschlossenen Tab (3d): Wer
 * binnen zwei Minuten wieder da ist, macht weiter.
 */
const KEY = 'happytask.focus.heartbeat'

/** Wie lange eine Rückkehr noch als „derselbe Fokus" gilt. */
export const FOCUS_GRACE_MS = 2 * 60 * 1000

export function writeFocusHeartbeat(at: Date = new Date()): void {
  try {
    localStorage.setItem(KEY, String(at.getTime()))
  } catch {
    // Privater Modus, gesperrter Speicher — dann gibt es eben kein Lebenszeichen.
  }
}

export function readFocusHeartbeat(): Date | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const ms = Number(raw)
    return Number.isFinite(ms) && ms > 0 ? new Date(ms) : null
  } catch {
    return null
  }
}

export function clearFocusHeartbeat(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // s. o.
  }
}

/**
 * Was mit einem laufenden Abschnitt geschehen soll, den ein Neustart vorfindet.
 *
 * - `resume` — das Lebenszeichen ist frisch, das Fokus-Fenster kommt zurück.
 * - `close` — es ist alt (oder fehlt); gebucht wird bis `at`.
 */
export function decideFocusRecovery(
  startTime: Date,
  heartbeat: Date | null,
  now: Date = new Date(),
): { action: 'resume' } | { action: 'close'; at: Date } {
  if (heartbeat && now.getTime() - heartbeat.getTime() <= FOCUS_GRACE_MS) {
    return { action: 'resume' }
  }
  // Ohne Lebenszeichen bleibt nur der Beginn — besser eine Zeit von null als
  // eine erfundene.
  return { action: 'close', at: heartbeat ?? startTime }
}
